<template>
  <div class="chart-container">
    <svg ref="svg" :width="width" :height="height" :viewBox="'0 0 ' + width + ' ' + height" style="max-width: 100%; height: auto;"></svg>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { group } from 'd3-array';

export default {
  name: 'ReportsChart',
  props: {
    data: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      width: 928,
      height: 450,
      marginTop: 10,
      marginRight: 10,
      marginBottom: 20,
      marginLeft: 40
    };
  },
  watch: {
    data: {
      handler: 'renderChart',
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.renderChart();
  },
  methods: {
    formatValue(x) {
      return isNaN(x) ? "N/A" : x;
    },
    createColorScale(groups) {
      const colorMap = {
        'Всего': '#FFA534',
        'Ошибки': '#ff0000',
        'Успешные': '#00a152'
      };
      const fallbackPalette = d3.schemeSpectral[groups.length] || [];
      return (name) => {
        if (colorMap[name]) return colorMap[name];
        const idx = groups.indexOf(name);
        return (idx >= 0 && fallbackPalette[idx]) ? fallbackPalette[idx] : '#ccc';
      };
    },
    renderChart() {
      if (!this.data || this.data.length === 0) return;

      d3.select(this.$refs.svg).selectAll("*").remove();
      d3.select(this.$el).selectAll('.chart-tooltip').remove();

      const svg = d3.select(this.$refs.svg);
      const tooltip = d3.select(this.$el)
        .append('div')
        .attr('class', 'chart-tooltip')
        .style('position', 'absolute')
        .style('pointer-events', 'none')
        .style('background', '#ffffff')
        .style('border', '1px solid #e9ecef')
        .style('padding', '4px 8px')
        .style('font-size', '12px')
        .style('border-radius', '4px')
        .style('color', '#343a40')
        .style('box-shadow', '0 2px 6px rgba(0,0,0,0.05)')
        .style('opacity', 0);

      svg.append("rect")
        .attr("width", this.width)
        .attr("height", this.height)
        .attr("fill", "#f8f9fa")

      const states = [...new Set(this.data.map(d => d.state))];
      const stateGroup = [...new Set(this.data.map(d => d.stateGroup))];

      const fx = d3.scaleBand()
        .domain(states)
        .rangeRound([this.marginLeft, this.width - this.marginRight])
        .paddingInner(0.1);

      const x = d3.scaleBand()
        .domain(stateGroup)
        .rangeRound([0, fx.bandwidth()])
        .padding(0.05);

      const color = this.createColorScale(stateGroup);

      const maxAvgTime = d3.max(this.data, d => d.avgTime);
      const legendHeight = 18;
      const legendSpacing = 8;
      const plotTop = this.marginTop + legendHeight + legendSpacing;
      const y = d3.scaleLinear()
        .domain([0, maxAvgTime]).nice()
        .rangeRound([this.height - this.marginBottom, plotTop]);

      svg.append("rect")
        .attr("x", this.marginLeft)
        .attr("y", plotTop)
        .attr("width", this.width - this.marginLeft - this.marginRight)
        .attr("height", this.height - plotTop - this.marginBottom)
        .attr("fill", "white")
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("stroke", "#e9ecef")
        .attr("stroke-width", 1);

      const gridLines = svg.append("g")
        .attr("class", "grid-lines");

      gridLines.selectAll("line.horizontal")
        .data(y.ticks())
        .join("line")
        .attr("class", "horizontal")
        .attr("x1", this.marginLeft)
        .attr("x2", this.width - this.marginRight)
        .attr("y1", d => y(d))
        .attr("y2", d => y(d))
        .attr("stroke", "#e9ecef")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2,2");

      const stateGroups = svg.append("g")
        .selectAll("g")
        .data(Array.from(group(this.data, d => d.state)))
        .join("g")
        .attr("transform", ([state]) => `translate(${fx(state)},0)`);

      stateGroups.selectAll("rect")
        .data(([, data]) => data)
        .join("rect")
        .attr("x", d => x(d.stateGroup))
        .attr("y", d => y(d.avgTime))
        .attr("width", x.bandwidth())
        .attr("height", d => y(0) - y(d.avgTime))
        .attr("fill", d => color(d.stateGroup))
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("stroke", "white")
        .attr("stroke-width", 1)
        .style("transition", "fill 0.2s ease")
        .on('mouseover', (d) => {
          tooltip
            .style('opacity', 1)
            .html(`${d.state || ''}<br/>${d.stateGroup || ''}: ${this.formatValue(d.avgTime || 0)}`);
        })
        .on('mousemove', () => {
          const [mx, my] = d3.mouse(this.$el);
          const container = this.$el;
          const containerRect = container.getBoundingClientRect();
          const tipWidth = tooltip.node() ? tooltip.node().offsetWidth : 0;
          const tipHeight = tooltip.node() ? tooltip.node().offsetHeight : 0;
          const pad = 12;
          const minLeft = 4;
          const minTop = 4;
          const maxLeft = containerRect.width - tipWidth - 4;
          const maxTop = containerRect.height - tipHeight - 4;
          const left = Math.max(minLeft, Math.min(mx + pad, maxLeft));
          const top = Math.max(minTop, Math.min(my - (tipHeight + 8), maxTop));
          tooltip
            .style('left', left + 'px')
            .style('top', top + 'px');
        })
        .on('mouseout', () => {
          tooltip.style('opacity', 0);
        });

      svg.append("g")
        .attr("transform", `translate(0,${this.height - this.marginBottom})`)
        .call(d3.axisBottom(fx).tickSizeOuter(0))
        .call(g => g.selectAll(".domain").remove())
        .attr("font-size", "12px");

      svg.append("g")
        .attr("transform", `translate(${this.marginLeft},0)`)
        .call(d3.axisLeft(y).ticks(null, "s"))
        .call(g => g.selectAll(".domain").remove())
        .attr("font-size", "12px");

      const legend = svg.append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${this.marginLeft}, ${this.marginTop})`);

      const legendItem = legend.selectAll("g")
        .data(stateGroup)
        .join("g")
        .attr("transform", (d, i) => `translate(${i * 140}, 0)`);

      legendItem.append("rect")
        .attr("width", 12)
        .attr("height", 12)
        .attr("rx", 2)
        .attr("ry", 2)
        .attr("fill", d => color(d));

      legendItem.append("text")
        .attr("x", 18)
        .attr("y", 10)
        .attr("font-size", 12)
        .attr("fill", "#6c757d")
        .text(d => d);
    }
  }
};
</script>

<style scoped>
.chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  position: relative;
}

:deep(.grid-lines line) {
  pointer-events: none;
}

:deep(.tick line) {
  stroke: #dee2e6;
}

:deep(.tick text) {
  fill: #6c757d;
}
</style>
